import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { AnaActionRouterService } from '@brand-lolly/app/_services/ana-action-router.service';
import {
	AnaCardRenderer,
	AnaEntityReference,
	AnaOutreachOfferPrices,
	AnaOutreachReview,
	AnaOutreachReviewCardComponent,
	AnaOutreachReviewEdit,
	AnaOutreachReviewRendererData
} from 'components';
import { LoDialogService } from 'lo-design-system';
import { EMPTY, interval, Observable, Subject } from 'rxjs';
import {
	catchError,
	exhaustMap,
	finalize,
	takeUntil,
	takeWhile
} from 'rxjs/operators';

import { toAiSearchPlatform } from '@brand-lolly/app/_models/platforms';
import { AnaAssistantService } from '@brand-lolly/app/_services/ana-assistant.service';
import { InfluencerActionsService } from '@brand-lolly/app/social-media-influencer/modules/campaigns/services/influencer-actions.service';
import { OutreachReviewService } from '@brand-lolly/app/social-media-influencer/modules/campaigns/services/outreach-review.service';
import {
	ConnectChannelSliderComponent,
	ConnectChannelSliderData
} from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/components/connect-channel/connect-channel-slider.component';

const POLL_EVERY_MS = 5_000;
// Stops after this long with NO new draft written — not after a fixed span: ten drafts once took 6 minutes and the card gave up at 3.
const POLL_WITHOUT_PROGRESS_MS = 3 * 60_000;

/** The server's refusal codes, in words a brand reads. */
const REFUSAL_COPY: Record<string, string> = {
	message_already_on_its_way:
		'This message is already on its way, so it can no longer be edited.',
	message_needs_a_body: 'A message needs some text before it can be saved.',
	no_first_message_for_this_creator:
		'There is no first message for this creator to edit.',
	no_outreach_to_start:
		'There is no outreach set up for this campaign to start yet.',
	outreach_did_not_start: 'Outreach did not start. Try again in a moment.',
	offers_changed_since_review:
		'The offers changed since this review was drawn. Check the new total and press Send again.',
	campaign_is_on_automated_outreach:
		'This campaign sends through automated outreach, not proposals.',
	nothing_to_send: 'There are no proposals left to send.',
	no_creator_can_be_reached:
		'None of these creators can be reached: none has an email address or a conversation to send the proposal on.',
	offers_need_a_price:
		'None of these offers has a price yet. Set a fee before sending.',
	offer_already_sent:
		'This offer has already been sent, so it can no longer be changed here.',
	campaign_not_found: 'This campaign could not be found.'
};

function refusalText(error: unknown): string {
	const detail =
		error instanceof HttpErrorResponse ? error.error?.detail : null;
	if (typeof detail === 'string' && detail) {
		if (REFUSAL_COPY[detail]) {
			return REFUSAL_COPY[detail];
		}
		// A department's own refusal (the start path) arrives as a code or a sentence.
		return /^[a-z0-9_]+$/.test(detail)
			? `${detail.charAt(0).toUpperCase()}${detail
					.slice(1)
					.replace(/_/g, ' ')}.`
			: detail;
	}
	return 'Something went wrong. Try again in a moment.';
}

/**
 * The brand app's outreach_review renderer: reads the review LIVE on mount and
 * after every action, so a card scrolled back to shows what is true now, and
 * draws it with the library card.
 *
 * Input `data`: the custom card's campaign_id and mode. The actions call the
 * campaign's own endpoints, naming the open conversation so the server records
 * what was done for Ana's next turn; a creator's name opens their discovery
 * profile slider, and once sending has started the card links to Messaging
 * Settings and opens the Messaging Center slider.
 */
@Component({
	standalone: true,
	selector: 'app-ana-outreach-review-renderer',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [AnaOutreachReviewCardComponent],
	template: `
		<app-ana-outreach-review-card
			[review]="review"
			[loading]="loading"
			[busy]="busy || starting"
			[starting]="starting"
			[error]="error"
			[editRevision]="editRevision"
			(startOutreach)="start()"
			(sendProposals)="sendProposals()"
			(saveMessage)="save($event)"
			(saveOffer)="savePrices($event)"
			(creatorClicked)="openCreator($event)"
			(connectChannel)="openMessagingSettings()"
			(openMessagingSettings)="openMessagingSettingsWithCenter()"
			(openMessaging)="openMessaging()"
		></app-ana-outreach-review-card>
	`
})
export class AnaOutreachReviewRendererComponent
	implements AnaCardRenderer, OnInit, OnDestroy
{
	@Input() data!: AnaOutreachReviewRendererData;
	/** Set by the custom card; nothing here reads it. */
	@Input() subject = '';

	review: AnaOutreachReview | null = null;
	/** Starts true: the review is read on init. */
	loading = true;
	busy = false;
	/** Start was pressed: held until the review read after it lands, so the button cannot be pressed twice. */
	starting = false;
	error: string | null = null;
	editRevision = 0;

	private polling = false;
	private readonly destroy$ = new Subject<void>();

	constructor(
		private service: OutreachReviewService,
		private changeDetector: ChangeDetectorRef,
		private actionRouter: AnaActionRouterService,
		private influencerActions: InfluencerActionsService,
		private assistant: AnaAssistantService,
		private loDialogService: LoDialogService
	) {}

	ngOnInit(): void {
		this.refresh();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	start(): void {
		if (this.busy || this.starting) {
			return;
		}
		this.starting = true;
		const settled = () => {
			this.starting = false;
			this.changeDetector.markForCheck();
		};
		this.act(
			this.service.start(this.campaignId, this.conversationId),
			() => this.refresh(settled),
			settled
		);
	}

	sendProposals(): void {
		this.act(
			this.service.sendProposals(
				this.campaignId,
				this.review?.total ?? null,
				this.conversationId
			),
			() => this.refresh()
		);
	}

	save(edit: AnaOutreachReviewEdit): void {
		this.act(
			this.service.edit(
				this.campaignId,
				edit.memberId,
				edit.subject,
				edit.body,
				this.conversationId
			),
			() => {
				this.editRevision += 1;
				this.refresh();
			}
		);
	}

	savePrices(prices: AnaOutreachOfferPrices): void {
		this.act(
			this.service.priceOffer(
				this.campaignId,
				prices,
				this.conversationId
			),
			() => {
				this.editRevision += 1;
				this.refresh();
			}
		);
	}

	/** The creator's discovery profile, the slider the campaign roster opens from a name. */
	openCreator(entity: AnaEntityReference): void {
		const influencerId = Number(entity.id);
		const platform = toAiSearchPlatform(entity.platform);
		if (!Number.isInteger(influencerId) || influencerId <= 0 || !platform) {
			return;
		}
		this.influencerActions.openDiscoveryProfileSlider(
			influencerId,
			platform
		);
	}

	/** The campaign's messaging settings, where a channel is connected. */
	openMessagingSettings(): void {
		if (!this.campaignId) {
			return;
		}
		// CONTRACT: the route id under /campaigns is the CAMPAIGN id (ProjectDetailsLayout.routeCampaignId).
		this.actionRouter.openFromChat([
			'/campaigns',
			this.campaignId,
			'messaging-settings'
		]);
	}

	/** The Messaging inbox filtered to this campaign (the bare /messaging route lands on Overview). */
	openMessaging(): void {
		// CONTRACT: ?campaignId= is a campaigns.id — the inbox list filter matches it on MessagingProgram.campaign_id.
		this.actionRouter.openFromChat(
			['/messaging', 'inbox'],
			this.campaignId
				? { queryParams: { campaignId: this.campaignId } }
				: {}
		);
	}

	/** The campaign's Messaging Settings page with its Messaging Center slider open on top. */
	openMessagingSettingsWithCenter(): void {
		if (!this.campaignId) {
			return;
		}
		// TRAP: through the action router, not the Router — on the page already
		// open the Router resolves false and fires no event, so the chat stayed
		// over the page and the Messaging Center never opened.
		this.actionRouter
			.openFromChat(['/campaigns', this.campaignId, 'messaging-settings'])
			.then(reached => {
				if (!reached) {
					return;
				}
				// CONTRACT: with campaignId set the slider reads the campaign's own program; projectId only matters to a search.
				this.loDialogService.openDrawer<
					ConnectChannelSliderComponent,
					ConnectChannelSliderData
				>(ConnectChannelSliderComponent, {
					data: { projectId: '', campaignId: this.campaignId }
				});
			});
	}

	/** The conversation this card is drawn in: the server records each act there for Ana's next turn. */
	private get conversationId(): string | null {
		return this.assistant.conversationId;
	}

	private get campaignId(): string {
		return this.data?.campaign_id ?? '';
	}

	private act(
		request: Observable<unknown>,
		done: () => void,
		failed?: () => void
	): void {
		if (this.busy) {
			return;
		}
		this.busy = true;
		this.error = null;
		this.changeDetector.markForCheck();
		request
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.busy = false;
					this.changeDetector.markForCheck();
				})
			)
			.subscribe({
				next: () => done(),
				error: (error: unknown) => {
					this.error = refusalText(error);
					// A moved total or a claimed message is only fixable against the review as it is NOW.
					this.refresh(failed);
				}
			});
	}

	/** Re-read the review; `settled` runs once that read has landed or failed. */
	private refresh(settled?: () => void): void {
		this.service
			.get(this.campaignId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: review => {
					this.apply(review);
					this.pollWhileWriting();
					settled?.();
				},
				error: (error: unknown) => {
					this.loading = false;
					this.error = this.error ?? refusalText(error);
					this.changeDetector.markForCheck();
					settled?.();
				}
			});
	}

	private apply(review: AnaOutreachReview): void {
		this.review = review;
		this.loading = false;
		this.changeDetector.markForCheck();
	}

	/** Re-read every 5 s while messages are still being written, until 3 minutes pass with none finished. */
	private pollWhileWriting(): void {
		if (this.polling || !this.review?.still_writing) {
			return;
		}
		this.polling = true;
		let writing = this.review.still_writing;
		let progressedAt = Date.now();
		interval(POLL_EVERY_MS)
			.pipe(
				takeUntil(this.destroy$),
				exhaustMap(() =>
					this.service
						.get(this.campaignId)
						.pipe(catchError(() => EMPTY))
				),
				takeWhile(review => {
					if (review.still_writing < writing) {
						writing = review.still_writing;
						progressedAt = Date.now();
					}
					return (
						review.still_writing > 0 &&
						Date.now() - progressedAt < POLL_WITHOUT_PROGRESS_MS
					);
				}, true),
				finalize(() => (this.polling = false))
			)
			.subscribe(review => this.apply(review));
	}
}
